import { useCallback, useEffect, useRef, useState } from "react";

export interface LogPage<E, C> {
  entries: E[];
  /// Pass back as `before`. `null` means this was the last page.
  next_cursor: C | null;
}

export interface PagedLog<E> {
  entries: E[];
  /// `null` until the first load settles, then `false` when this caller may not
  /// read the log — the section hides rather than showing an error.
  available: boolean | null;
  hasMore: boolean;
  loadingMore: boolean;
  error: string | null;
  loadMore: () => void;
}

interface Source<E extends { id: unknown }, C, P extends LogPage<E, C>> {
  /// Reloads from the first page when it changes; `null` loads nothing.
  key: string | null;
  /// The first page, or `null` when the caller may not read the log.
  first: () => Promise<P | null>;
  /// A later page. Throws, so a failure reads as an error, not as the end.
  next: (before: C) => Promise<P>;
  /// Fetches what a page's rows name, and returns the step that stores it:
  /// that step runs with the rows, and never for a load that was superseded.
  onPage: (page: P, first: boolean) => Promise<() => void>;
  errorLabel: string;
}

/// A newest-first log behind a keyset cursor: the first page, Load more, and
/// rows deduped by id across pages.
export function usePagedLog<
  E extends { id: unknown },
  C,
  P extends LogPage<E, C>,
>(source: Source<E, C, P>): PagedLog<E> {
  // The calls change identity every render; only `key` decides a reload.
  const latest = useRef(source);
  latest.current = source;
  const { key } = source;

  const [entries, setEntries] = useState<E[]>([]);
  const [cursor, setCursor] = useState<C | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (key === null) {
      setAvailable(false);
      return;
    }
    let live = true;
    setAvailable(null);
    setError(null);
    void latest.current.first().then(async (page) => {
      const commit = page && (await latest.current.onPage(page, true));
      if (!live) return;
      commit?.();
      setEntries(page?.entries ?? []);
      setCursor(page?.next_cursor ?? null);
      setAvailable(page !== null);
    });
    return () => {
      live = false;
    };
  }, [key]);

  const loadMore = useCallback(() => {
    if (key === null || cursor === null || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    latest.current
      .next(cursor)
      .then(async (page) => {
        (await latest.current.onPage(page, false))();
        // Keyset paging cannot overlap, but a re-fetch of the same cursor
        // could — dedupe so it stays harmless.
        setEntries((prev) => {
          const seen = new Set(prev.map((e) => e.id));
          return [...prev, ...page.entries.filter((e) => !seen.has(e.id))];
        });
        setCursor(page.next_cursor);
      })
      .catch(() => setError(latest.current.errorLabel))
      .finally(() => setLoadingMore(false));
  }, [key, cursor, loadingMore]);

  return {
    entries,
    available,
    hasMore: cursor !== null,
    loadingMore,
    error,
    loadMore,
  };
}
